import { appWithTranslation } from 'next-i18next';
import 'dayjs/locale/ru';
import 'swiper/css';
import 'swiper/swiper-bundle.css';
import GlobalFonts from '../styled-components-config/fonts';
import { SkeletonTheme } from 'react-loading-skeleton';
import { I18nextProvider } from 'react-i18next';
import i18n from '@core/i18n';
import { ThemeProvider } from 'styled-components';
import { GlobalStyle, theme } from '../styled-components-config';
import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import RootStore, { initializeStore } from '@core/stores/root-store';
import { RootStoreContext } from '@core/contexts/stores-context';
import { Layout } from '@components/entities';
import { AppProps } from 'next/app';
import ErrorBoundary from '@views/error-boundary';
import { observer } from 'mobx-react-lite';
import nextI18nextConfig from '../../next-i18next.config';
import { isProductDetailPath } from '@core/utils/navigation-history';

const WrappedApp = ({ Component, pageProps }: AppProps) => {
	const [store, setStore] = useState<RootStore | null>(null);
	const router = useRouter();
	const currentRouteRef = useRef(router.asPath);

	useEffect(() => {
		const rootStore = initializeStore(pageProps);
		rootStore.hydrate(pageProps);
		setStore(rootStore);
	}, [pageProps]);

	useEffect(() => {
		if (!store) {
			return;
		}

		const rememberCurrentRoute = (url: string) => {
			currentRouteRef.current = url;

			if (!isProductDetailPath(url)) {
				store.core.setProductReturnRoute(url);
			}
		};

		const initialRoute = currentRouteRef.current;

		if (!isProductDetailPath(initialRoute)) {
			rememberCurrentRoute(initialRoute);
		}

		const handleRouteChangeStart = (url: string) => {
			if (
				isProductDetailPath(url) &&
				!isProductDetailPath(currentRouteRef.current)
			) {
				store.core.setProductReturnRoute(currentRouteRef.current);
			}
		};

		router.events.on('routeChangeStart', handleRouteChangeStart);
		router.events.on('routeChangeComplete', rememberCurrentRoute);

		return () => {
			router.events.off('routeChangeStart', handleRouteChangeStart);
			router.events.off('routeChangeComplete', rememberCurrentRoute);
		};
	}, [router.events, store]);

	if (!store) {
		return null;
	}

	return (
		<div className="App">
			<RootStoreContext.Provider value={store}>
				<GlobalFonts />
				<SkeletonTheme>
					<I18nextProvider i18n={i18n}>
						<ThemeProvider theme={theme}>
							<GlobalStyle />
							<ErrorBoundary>
								<Layout>
									<Component {...pageProps} />
								</Layout>
							</ErrorBoundary>
						</ThemeProvider>
					</I18nextProvider>
				</SkeletonTheme>
			</RootStoreContext.Provider>
		</div>
	);
};

export default appWithTranslation(observer(WrappedApp), nextI18nextConfig);
